import type { Node, Edge } from '../../../../core/models/scene';
import { useDiagramStore } from '../../../../stores/diagramStore';
import { getNodeMutationManager } from '../../../../core/commands/mutations/NodeMutationManager';
import { encodeStateVector } from 'yjs';
import { PROCESS_OPERATION_FIELDS, type ProcessOperationName } from './ProcessOperationContract';

export class ProcessChangeError extends Error {
  constructor(readonly code: 'AMBIGUOUS_TARGET' | 'INVALID_CHANGE' | 'STALE_PROCESS', message: string) { super(message); }
}
const label = (node: Node) => String(node.attrs?.label ?? node.attrs?.name ?? node.props?.label ?? node.name ?? '');
const STEP_TYPES = new Set(['process', 'decision', 'terminator', 'data', 'document', 'flowchart-shape', 'bpmn-shape', 'start-end']);
export const isProcessStep = (node: Node) => STEP_TYPES.has(node.type);
const norm = (value: string) => value.trim().toLowerCase();
export function resolveProcessStep(nodes: Node[], query: string): Node {
  nodes = nodes.filter(isProcessStep);
  const byId = nodes.find(node => node.id === query);
  if (byId) return byId;
  const exact = nodes.filter(node => norm(label(node)) === norm(query));
  const matches = exact.length ? exact : nodes.filter(node => norm(label(node)).includes(norm(query)));
  if (matches.length > 1) throw new ProcessChangeError('AMBIGUOUS_TARGET', `Multiple steps match "${query}". Choose the stable step ID before applying changes.`);
  if (!matches.length) throw new ProcessChangeError('INVALID_CHANGE', `Could not find step "${query}".`);
  return matches[0];
}
/** A model-chosen ID is not evidence that the human resolved a duplicate label. */
export function validateProcessTargetIntent(
  operations: Record<string, unknown>[], nodes: Node[],
  intent: { selectedNodeIds: string[]; userRequest?: string },
): void {
  const steps = nodes.filter(isProcessStep);
  // Explicit ID syntax belongs to the current human message, not tool arguments
  // or assistant history. IDs remain case sensitive and require an exact token.
  const userIds = new Set(Array.from((intent.userRequest ?? '').matchAll(/\bid\s*:\s*([a-zA-Z0-9_-]+)(?=$|[\s,;!?])/gi), match => match[1]));
  for (const operation of operations.filter(raw => raw && typeof raw === 'object' && !Array.isArray(raw))) for (const field of ['target', 'from', 'to', 'after']) {
    if (typeof operation[field] !== 'string') continue;
    const node = steps.find(step => step.id === String(operation[field]).trim());
    if (!node) continue; // Label ambiguity is checked by ordinary preflight.
    const duplicates = steps.filter(step => norm(label(step)) === norm(label(node)));
    if (duplicates.length < 2) continue;
    const selected = duplicates.filter(step => intent.selectedNodeIds.includes(step.id));
    if ((selected.length === 1 && selected[0].id === node.id) || userIds.has(node.id)) continue;
    throw new ProcessChangeError('AMBIGUOUS_TARGET', `Multiple steps are named "${label(node)}". Select the intended step or supply its explicit ID before applying changes.`);
  }
}
export function validateProcessOperations(operations: Record<string, unknown>[], nodes: Node[], edges: Edge[]): void {
  let steps = nodes.filter(isProcessStep).map(node => ({ ...node, attrs: { ...node.attrs } }));
  let flows = edges.map(edge => ({ ...edge }));
  const text = (raw: Record<string, unknown>, key: string): string => {
    if (typeof raw[key] !== 'string' || !raw[key].trim()) throw new ProcessChangeError('INVALID_CHANGE', `${String(raw.op)} needs ${key}.`);
    return raw[key].trim();
  };
  operations.forEach((raw, index) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new ProcessChangeError('INVALID_CHANGE', 'Each operation must be an object.');
    const op = text(raw, 'op');
    const contract = Object.prototype.hasOwnProperty.call(PROCESS_OPERATION_FIELDS, op) ? PROCESS_OPERATION_FIELDS[op as ProcessOperationName] : undefined;
    if (!contract) throw new ProcessChangeError('INVALID_CHANGE', `Unsupported operation "${op}".`);
    const allowed = new Set<string>(['op', ...contract.required, ...contract.optional]);
    for (const key of Object.keys(raw)) {
      if (!allowed.has(key)) throw new ProcessChangeError('INVALID_CHANGE', `${op} does not accept ${key}. Use flat operation fields.`);
      if (key !== 'op') text(raw, key);
    }
    for (const key of contract.required) text(raw, key);
    const resolve = (key: string) => resolveProcessStep(steps, text(raw, key));
    if (op === 'add_step' || op === 'insert_step') {
      const name = text(raw, 'label');
      if (raw.stepKind !== undefined && !['task', 'process', 'decision', 'start', 'end'].includes(String(raw.stepKind))) throw new ProcessChangeError('INVALID_CHANGE', 'Unsupported stepKind.');
      const id = `pending-process-step-${index}`;
      if (op === 'insert_step') {
        const from = resolve('from'), to = resolve('to');
        const matches = flows.filter(flow => flow.from === from.id && flow.to === to.id);
        if (matches.length > 1) throw new ProcessChangeError('AMBIGUOUS_TARGET', 'Multiple flows connect these steps. Clarify the flow before changing it.');
        const edge = matches[0];
        if (!edge) throw new ProcessChangeError('INVALID_CHANGE', `No flow from "${label(from)}" to "${label(to)}".`);
        flows = flows.filter(flow => flow.id !== edge.id);
        flows.push({ ...edge, from: from.id, to: id }, { ...edge, id: `${id}-out`, from: id, to: to.id });
      } else if (raw.after !== undefined) {
        const after = resolve('after');
        flows.push({ id: `${id}-in`, type: 'line', from: after.id, to: id });
      }
      steps.push({ id, type: 'flowchart-shape', x: 0, y: 0, attrs: { label: name } });
    } else if (op === 'rename') {
      const target = resolve('target');
      target.attrs = { ...target.attrs, label: text(raw, 'label') };
    } else if (op === 'remove_step') {
      const target = resolve('target');
      steps = steps.filter(step => step.id !== target.id);
      flows = flows.filter(flow => flow.from !== target.id && flow.to !== target.id);
    } else if (op === 'connect' || op === 'disconnect') {
      const from = resolve('from'), to = resolve('to');
      const matches = flows.filter(flow => flow.from === from.id && flow.to === to.id);
      if (op === 'disconnect' && matches.length > 1) throw new ProcessChangeError('AMBIGUOUS_TARGET', 'Multiple flows connect these steps. Clarify the flow before changing it.');
      const edge = matches[0];
      if ((op === 'connect' && edge) || (op === 'disconnect' && !edge)) throw new ProcessChangeError('INVALID_CHANGE', op === 'connect' ? 'These steps already have a flow.' : 'No flow connects these steps.');
      if (op === 'connect') flows.push({ id: `pending-flow-${index}`, type: 'line', from: from.id, to: to.id });
      else flows = flows.filter(flow => flow.id !== edge!.id);
    } else throw new ProcessChangeError('INVALID_CHANGE', `Unsupported operation "${op}".`);
  });
}
export function captureProcessBaseline(): () => void {
  const manager = getNodeMutationManager();
  const doc = manager.getDoc();
  const version = manager.getDocumentBindingVersion?.() ?? 0;
  const vector = doc ? String(encodeStateVector(doc)) : null;
  const signature = () => {
    const state = useDiagramStore.getState();
    return JSON.stringify([
      state.nodes.map(node => [node.id, node.type, label(node), node.attrs?.lane, node.attrs?.variant]).sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
      state.edges.map(edge => [edge.id, edge.from, edge.to, edge.type, edge.attrs?.label]).sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
    ]);
  };
  const original = signature();
  return () => {
    if (manager.getDoc() !== doc || (manager.getDocumentBindingVersion?.() ?? 0) !== version || signature() !== original || (doc && String(encodeStateVector(doc)) !== vector)) {
      throw new ProcessChangeError('STALE_PROCESS', 'The process changed after this proposal. Refresh context and propose the changes again.');
    }
    if (useDiagramStore.getState().canWrite === false) throw new ProcessChangeError('STALE_PROCESS', 'Write access changed. Refresh access before applying.');
  };
}
